/**
 * Automated primary-photo selection (ported from Number Hunter's
 * numberhunter-todo.md item 3, generalized to arbitrary item keys).
 *
 * For every captured item in a hunt, decides which of its photos becomes primary:
 *   3a. Maximum bipartite matching (items <-> locations, Kuhn's algorithm)
 *       so as many items as possible land on a location unique to them.
 *   3b. Within whichever location an item lands on, the specific photo is
 *       chosen by qualityScore (see imageUtils.js).
 *   3c. Items that can't get a unique location (more items than locations
 *       in their cluster) fall back to a min-cost-flow assignment that
 *       spreads them evenly across already-used locations instead of piling
 *       onto one.
 *
 * Fully rule-based — no manual judgment calls.
 */

// ── Grouping ─────────────────────────────────────────────────────────────

function groupByItem(photos) {
  const map = new Map();
  for (const p of photos) {
    if (!map.has(p.itemKey)) map.set(p.itemKey, []);
    map.get(p.itemKey).push(p);
  }
  return map;
}

function candidateLocations(photosForItem) {
  return [...new Set(photosForItem.map(p => p.location).filter(Boolean))].sort();
}

// ── 3a: maximum bipartite matching (Kuhn's algorithm) ───────────────────────
// Deterministic: items processed in the caller-supplied order, each item's
// candidate locations tried in alphabetical order.

function maxBipartiteMatching(items, adjacency) {
  const locationToItem = new Map(); // location -> itemKey
  const itemToLocation = new Map(); // itemKey -> location

  function tryAugment(item, visited) {
    for (const loc of adjacency.get(item)) {
      if (visited.has(loc)) continue;
      visited.add(loc);
      const occupant = locationToItem.get(loc);
      if (occupant === undefined || tryAugment(occupant, visited)) {
        locationToItem.set(loc, item);
        itemToLocation.set(item, loc);
        return true;
      }
    }
    return false;
  }

  for (const item of items) {
    tryAugment(item, new Set());
  }

  return itemToLocation;
}

// ── 3c: min-cost flow fallback for leftover items ────────────────────────────
//
// Each location's k-th assigned slot is modeled as its own unit-capacity
// edge with increasing cost (start + k) — the standard trick for expressing
// a convex "spread load evenly" objective in min-cost flow. Successive
// shortest-path augmentation then naturally prefers an under-used location
// over an already-loaded one, and can be solved with plain Bellman-Ford/SPFA
// since the graph here is tiny (bounded by a hunt's item count).

function minCostFlowAssign(unmatchedItems, adjacency, initialLoad) {
  const result = new Map();
  if (unmatchedItems.length === 0) return result;

  const SOURCE = 'source';
  const SINK   = 'sink';
  const itemNode = key => `i:${key}`;
  const locationNode = loc => `l:${loc}`;

  const graph = new Map();
  function ensureNode(id) { if (!graph.has(id)) graph.set(id, []); }
  function addEdge(from, to, cap, cost) {
    ensureNode(from); ensureNode(to);
    const edge = { to, cap, cost, from };
    const revEdge = { to: from, cap: 0, cost: -cost, from: to };
    edge.rev = revEdge;
    revEdge.rev = edge;
    graph.get(from).push(edge);
    graph.get(to).push(revEdge);
  }

  const locations = new Set();
  for (const key of unmatchedItems) {
    addEdge(SOURCE, itemNode(key), 1, 0);
    for (const loc of adjacency.get(key)) {
      addEdge(itemNode(key), locationNode(loc), 1, 0);
      locations.add(loc);
    }
  }
  for (const loc of locations) {
    const start = initialLoad.get(loc) ?? 0;
    for (let k = 0; k < unmatchedItems.length; k++) {
      addEdge(locationNode(loc), SINK, 1, start + k);
    }
  }

  function findShortestAugmentingPath() {
    const dist = new Map([[SOURCE, 0]]);
    const prevEdge = new Map();
    const queue = [SOURCE];
    const inQueue = new Set([SOURCE]);

    while (queue.length) {
      const u = queue.shift();
      inQueue.delete(u);
      for (const edge of graph.get(u) ?? []) {
        if (edge.cap <= 0) continue;
        const nd = dist.get(u) + edge.cost;
        if (nd < (dist.get(edge.to) ?? Infinity)) {
          dist.set(edge.to, nd);
          prevEdge.set(edge.to, edge);
          if (!inQueue.has(edge.to)) {
            queue.push(edge.to);
            inQueue.add(edge.to);
          }
        }
      }
    }

    if (!dist.has(SINK)) return null;
    const path = [];
    let node = SINK;
    while (node !== SOURCE) {
      const edge = prevEdge.get(node);
      path.push(edge);
      node = edge.from;
    }
    return path;
  }

  for (let i = 0; i < unmatchedItems.length; i++) {
    const path = findShortestAugmentingPath();
    if (!path) break; // shouldn't happen — slot capacity always covers this many items
    for (const edge of path) {
      edge.cap -= 1;
      edge.rev.cap += 1;
    }
  }

  // Decode: for each item, find which item->location edge got used
  // (its residual capacity dropped to 0).
  for (const key of unmatchedItems) {
    for (const edge of graph.get(itemNode(key)) ?? []) {
      if (edge.to.startsWith('l:') && edge.cap === 0) {
        result.set(key, edge.to.slice(2));
        break;
      }
    }
  }

  return result;
}

// ── 3b: best photo at a given location ──────────────────────────────────────

function pickBestPhoto(candidates) {
  return [...candidates].sort((a, b) => {
    const qa = a.qualityScore ?? -1;
    const qb = b.qualityScore ?? -1;
    if (qb !== qa) return qb - qa;
    if (a.addedAt !== b.addedAt) return b.addedAt.localeCompare(a.addedAt);
    return a.id.localeCompare(b.id);
  })[0];
}

// ── Entry point ──────────────────────────────────────────────────────────────

/**
 * @param {Array} photos - full photo list for one hunt (any/all item keys)
 * @param {(a: string, b: string) => number} [comparator] - sort order for
 *   item keys; defaults to plain string comparison. Pass huntDomain's
 *   comparatorFor(huntType) for a numeric-aware order on Numbers hunts.
 * @returns {Map<string, string>} itemKey -> id of the photo that should be primary
 */
export function computeAutoSelection(photos, comparator = (a, b) => a.localeCompare(b)) {
  const byItem = groupByItem(photos);
  const items = [...byItem.keys()].sort(comparator);
  if (items.length === 0) return new Map();

  const adjacency = new Map();
  for (const key of items) adjacency.set(key, candidateLocations(byItem.get(key)));

  const matched = maxBipartiteMatching(items, adjacency);

  const unmatched = items.filter(key => !matched.has(key));
  const initialLoad = new Map();
  for (const loc of matched.values()) initialLoad.set(loc, (initialLoad.get(loc) ?? 0) + 1);

  const fallback = minCostFlowAssign(unmatched, adjacency, initialLoad);

  const itemToLocation = new Map([...matched, ...fallback]);

  const assignment = new Map();
  for (const key of items) {
    const loc = itemToLocation.get(key);
    const candidates = byItem.get(key).filter(p => p.location === loc);
    assignment.set(key, pickBestPhoto(candidates).id);
  }

  return assignment;
}
